import { inject, injectable, interfaces } from 'inversify';
import qs from 'qs';
import { ACDLError } from '../../../../../models/error.model';
import { IMainApiService } from '../../../../../services/api.service';
import { IApiService } from '../../../../../services/api.service.base';
import { QuestionType } from '../../../../annotation/question.model';
import { ProjectStatus } from '../../../../projects/projects.model';
import { ToolDeleteStrategy } from '../../../projectDetails.models';
import { IAnnotationType, IAnswer, IQuestion, IToolAffect, IToolUsage } from '../projectDetailsTools.models';

interface IAnnotationViewTabDto {
  annotationTypes: IAnnotationType[];
  questions: IQuestion[];
  status: ProjectStatus;
}

interface IAddQuestionRequest {
  projectId: string;
  questionId: string;
  text: string;
  answerType: QuestionType;
  isRequired: boolean;
  answers: IAnswer[];
  scopes: string[];
}

interface IUpdateQuestionRequest {
  projectId: string;
  questionId: string;
  questionType: string;
  text: string;
  isRequired: boolean;
  answers: IAnswer[];
  scopes: string[];
  order: number;
  strategy: ToolDeleteStrategy;
}

interface IUpdateQuestionsOrderingRequest {
  projectId: string;
  orders: { id: string; order: number }[];
}

interface IGetQuestionUsageRequest {
  questionId: string;
}

interface IGetQuestionAnswersUsageRequest {
  questionId: string;
  answerIds: string[];
  scopesIds: string[];
}

interface IDeleteQuestionRequest {
  projectId: string;
  questionId: string;
  strategy: ToolDeleteStrategy;
}

interface ICheckQuestionTextUniquenessRequest {
  projectId: string;
  questionId: string;
  isAttribute: boolean;
  text: string;
}

interface IGetProjectDetailsAnnotationViewRequest {
  projectId: string;
}

interface IGetQuestionAffectRequest {
  questionId: string;
}

interface IGetQuestionAnswersAffectRequest {
  questionId: string;
  answerIds: string[];
  scopesIds: string[];
}

export interface IQuestionsApiService {
  addQuestionAsync(request: IAddQuestionRequest): Promise<void | ACDLError>;
  updateQuestionAsync(request: IUpdateQuestionRequest): Promise<void | ACDLError>;
  updateQuestionsOrderAsync(request: IUpdateQuestionsOrderingRequest): Promise<void | ACDLError>;
  getQuestionUsage(request: IGetQuestionUsageRequest): Promise<IToolUsage | ACDLError>;
  getQuestionAnswersUsage(request: IGetQuestionAnswersUsageRequest): Promise<IToolUsage | ACDLError>;
  deleteQuestionAsync(request: IDeleteQuestionRequest): Promise<void | ACDLError>;
  checkQuestionTextUniqueness(request: ICheckQuestionTextUniquenessRequest): Promise<boolean | ACDLError>;
  getQuestionAffect(request: IGetQuestionAffectRequest): Promise<IToolAffect | ACDLError>;
  getQuestionAnswersAffect(request: IGetQuestionAnswersAffectRequest): Promise<IToolAffect | ACDLError>;

  getProjectDetailsAnnotationViewAsync(request: IGetProjectDetailsAnnotationViewRequest): Promise<IAnnotationViewTabDto | ACDLError>;
}

export namespace IQuestionsApiService {
  export const $: interfaces.ServiceIdentifier<IQuestionsApiService> = Symbol('PROJECT_DETAILS_QUESTIONS_API_SERVICE');
}

@injectable()
export class QuestionsApiService implements IQuestionsApiService {
  constructor(@inject(IMainApiService.$) private readonly apiService: IApiService) {}

  async addQuestionAsync(request: IAddQuestionRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Questions/Add', request);
  }

  async updateQuestionAsync(request: IUpdateQuestionRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Questions/Update', request);
  }

  async updateQuestionsOrderAsync(request: IUpdateQuestionsOrderingRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Questions/UpdateQuestionsOrdering', request);
  }

  async getQuestionUsage(request: IGetQuestionUsageRequest): Promise<IToolUsage | ACDLError> {
    return await this.apiService.getAsync<IToolUsage>('/Questions/GetUsage', { params: request });
  }

  async getQuestionAnswersUsage(request: IGetQuestionAnswersUsageRequest): Promise<IToolUsage | ACDLError> {
    return await this.apiService.getAsync<IToolUsage>('/Questions/GetAnswersUsage', {
      params: request,
      paramsSerializer: (params: object) => qs.stringify(params),
    });
  }

  async deleteQuestionAsync(request: IDeleteQuestionRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Questions/Delete', request);
  }

  async checkQuestionTextUniqueness(request: ICheckQuestionTextUniquenessRequest): Promise<boolean | ACDLError> {
    return await this.apiService.getAsync<boolean>('/Questions/CheckQuestionTextUniqueness', { params: request });
  }

  async getProjectDetailsAnnotationViewAsync(request: IGetProjectDetailsAnnotationViewRequest): Promise<IAnnotationViewTabDto | ACDLError> {
    return await this.apiService.getAsync<IAnnotationViewTabDto>('/Projects/AnnotationViewTab', { params: request });
  }

  async getQuestionAffect(request: IGetQuestionAffectRequest): Promise<IToolAffect | ACDLError> {
    return await this.apiService.getAsync<IToolAffect>('/Questions/GetAffect', { params: request });
  }

  async getQuestionAnswersAffect(request: IGetQuestionAnswersAffectRequest): Promise<IToolAffect | ACDLError> {
    return await this.apiService.getAsync<IToolAffect>('/Questions/GetAnswersAffect', {
      params: request,
      paramsSerializer: (params: object) => qs.stringify(params),
    });
  }
}
